
import { defineCommand } from '../../../concepts/commands/CommandDefinition';
import { CommandDispatcher } from '../../../concepts/commands/CommandDispatcher';
import { inCommands } from '../../../concepts/commands/CommandRegistry';
import { ObjectManager } from '../../../concepts/objects';
import { resolveSubjects } from '../../canvas/commands/CommandUtil';
import { bringToFront, place, unplace } from '../../canvas/commands/OrderCommands';
import { select } from '../../canvas/commands/SelectionCommands';
import { Physical } from '../../canvas/entities/Physical';
import { Physicals } from '../../canvas/entities/Physicals';
import { GridSystem } from '../../canvas/GridSystem';
import { SpatialIndex } from '../../canvas/utilities/SpatialIndex';
import { ActivityFrameAddon } from './addons/ActivityFrameAddon';
import { Frame } from './Frame';

export const frameCreate = defineCommand(
    'frameCreate',
    'Frame: Create',
    (r, [targetIds, padding]: [string[]?, number?]) => {

        const objects = r.call(ObjectManager);
        const dispatch = r.call(CommandDispatcher);
        const grid = r.call(GridSystem);
        const spatial = r.call(SpatialIndex);
        const subjects = resolveSubjects(r, targetIds);

        if (subjects.length === 0) {
            objects.update(u => {
                Physicals.createAtCameraPosition(r, Frame, frame => {
                    frame.add(u.create(ActivityFrameAddon));
                    frame.title = 'Frame';
                    frame.width = 1600;
                    frame.height = 900;
                });
            });
        }
        else {
            const tops = Physicals.top(subjects);
            objects.update(u => {

                const area = grid.snap(Physicals.bounds(tops).inflate(padding ?? 20));

                const frame = u.create(Frame);
                frame.add(u.create(ActivityFrameAddon));
                frame.setFromFigure(area.points());
                frame.title = 'Frame';
                objects.getRoot().add(frame);

                // SpatialIndex is updated via triggers, but they will execute at the end of the current cycle,
                // so we need to manually update the spatial index here so that the following commands work.
                // This isn't great, but it's an unfortunate side effect of the current architecture.
                spatial.invalidate(frame);

                dispatch(unplace, [tops.map(x => x.id)]);
                dispatch(bringToFront, [tops.map(x => x.id)]);
                dispatch(place, [tops.map(x => x.id)]);
                dispatch(select, [[frame.id]]);

            });
        }
    }
);

export const frameRemove = defineCommand(
    'frameRemove',
    'Frame: Remove',
    (r, [targetIds]: [string[]?]) => {

        const subjects = resolveSubjects(r, targetIds);
        if (!subjects.length) return;
        if (!subjects.every(x => x.is(Frame))) return;

        const objects = r.call(ObjectManager);

        objects.update(u => {

            for (const frame of subjects) {

                for (const ch of frame.children) {
                    const pch = ch.as(Physical);
                    if (pch) {
                        // Remember globally where the child was
                        const chf = pch.createFigure();
                        // Remove and add to before the frame
                        frame.parentPhysical.addBefore(frame, pch.removeSelf());
                        // Reposition to where it was
                        pch.setFromFigure(chf);
                    }
                    else {
                        frame.remove(ch);
                    }
                }

                frame.removeSelf();
                u.delete(frame);
            }
        });
    }
);

export const frameEmpty = defineCommand(
    'frameEmpty',
    'Frame: Empty',
    (r, [targetIds]: [string[]?]) => {

        const subjects = resolveSubjects(r, targetIds);
        if (!subjects.length) return;
        if (!subjects.every(x => x.is(Frame))) return;

        const objects = r.call(ObjectManager);

        objects.update(u => {

            for (const frame of subjects) {

                for (const ch of frame.children) {
                    const pch = ch.as(Physical);
                    if (pch) {
                        frame.remove(pch);
                        u.delete(pch);
                    }
                }
            }
        });
    }
);

export const frameToggleHidden = defineCommand(
    'frameToggleHidden',
    'Frame: Toggle Hidden',
    (r, [targetIds, hidden]: [string[]?, boolean?]) => {
        const subjects = Physicals.polarize(resolveSubjects(r, targetIds), Frame);
        if (!subjects.length) return;

        const objects = r.call(ObjectManager);

        objects.update(() => {
            for (const s of subjects) {
                const a = s.addon(ActivityFrameAddon);
                if (a) {
                    a.hidden = hidden ?? !a.hidden;
                }
            }
        });
    },
    [],
);

export const inFrameCommands = () => inCommands(
    frameToggleHidden,
    frameCreate,
    frameRemove,
    frameEmpty,
);

